'use client';

import { Trans } from '@kit/ui/trans';

/**
 * Shown when a guest upgrade matched by EMAIL has a different phone on file
 * than the one the customer typed (phone counterpart of EmailMismatchBanner).
 * The on-file number is the tenant's WhatsApp reach — often WhatsApp-proven
 * for uploaded guests — so we ask instead of silently overwriting it. The
 * form stays mounted below; the resolve callbacks replay the submission with
 * phoneConflictResolution set.
 */
export function PhoneMismatchBanner({
  phoneHint,
  typedPhone,
  loading,
  onUseExisting,
  onOverride,
  onDismiss,
}: {
  phoneHint: string;
  typedPhone: string;
  loading: boolean;
  onUseExisting: () => void;
  onOverride: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="border-primary/30 bg-primary/5 rounded-lg border p-4 text-sm">
      <p className="text-foreground font-medium">
        <Trans i18nKey="auth:phoneMismatchHeading" defaults="Is this you?" />
      </p>

      <p className="text-muted-foreground mt-1">
        <Trans
          i18nKey="auth:phoneMismatchBody"
          values={{ phoneHint, typedPhone }}
          components={{ strong: <strong className="text-foreground" /> }}
          defaults="We already have <strong>{{phoneHint}}</strong> on file for this account. You typed <strong>{{typedPhone}}</strong>. Which number do you want to keep?"
        />
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={loading}
          onClick={onUseExisting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50"
          data-test="phone-mismatch-use-existing"
        >
          <Trans
            i18nKey="auth:phoneMismatchUseExisting"
            defaults="Keep the number on file"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onOverride}
          className="border-input bg-background hover:bg-accent inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50"
          data-test="phone-mismatch-override"
        >
          <Trans
            i18nKey="auth:phoneMismatchOverride"
            defaults="No, use the new number"
          />
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded-md px-3 py-2 text-sm disabled:opacity-50"
        >
          <Trans i18nKey="common:cancel" defaults="Cancel" />
        </button>
      </div>
    </div>
  );
}
